/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/
// React
import React from 'react';
import { Link } from 'react-router-dom';
// Actions
import { publishComment, unpublishComment } from '../../main/comments/actions';
// BASE Components
import BASESvgIcon from '../../../@BASE/core/BASESvgIcon';
// Material UI Components
import { lighten } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import CardHeader from '@mui/material/CardHeader';
import Chip from '@mui/material/Chip';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Tooltip from '@mui/material/Tooltip';
// Custom Components
import moment from 'moment';
import authRoles from '../../auth/authRoles';

function CommentStatusBar(props) {
  const { comment, internality } = props;

  const handleScopeIcon = (scope) => {
    switch (scope) {
      case 'course':
        return 'heroicons-outline:view-grid';
      case 'lesson':
        return 'heroicons-outline:book-open';
      case 'section':
        return 'heroicons-outline:folder';
      default:
        return 'heroicons-outline:document-text';
    }
  };

  const handleReviewLabel = (review, internal) => {
    switch (review) {
      case 'dev_alpha_internal':
        if (!internal) {
          return 'ALPHA';
        } else {
          return 'ALPHA (I)';
        }
      case 'dev_alpha':
        return 'ALPHA';
      case 'dev_alpha_revision':
        if (!internal) {
          return 'ALPHA';
        } else {
          return 'ALPHA (R)';
        }
      case 'dev_beta':
        return 'BETA';
      case 'dev_beta_revision':
        if (!internal) {
          return 'BETA';
        } else {
          return 'BETA (R)';
        }
      case 'prod_alpha_internal':
        if (!internal) {
          return 'ALPHA';
        } else {
          return 'ALPHA (I)';
        }
      case 'prod_alpha':
        return 'ALPHA';
      case 'prod_alpha_revision':
        if (!internal) {
          return 'ALPHA';
        } else {
          return 'ALPHA (R)';
        }
      case 'prod_beta':
        return 'BETA';
      case 'prod_beta_revision':
        if (!internal) {
          return 'BETA';
        } else {
          return 'BETA (R)';
        }
      case 'trials_pre':
        return 'QA';
      default:
        return 'Final';
    }
  };

  const handleReviewColor = (review, internal) => {
    switch (review) {
      case 'dev_alpha_internal':
        if (!internal) {
          return '#80F2E7';
        } else {
          return '#FF55BF';
        }
      case 'dev_alpha':
        return '#80F2E7';
      case 'dev_alpha_revision':
        if (!internal) {
          return '#80F2E7';
        } else {
          return '#FF55BF';
        }
      case 'dev_beta':
        return '#48BF91';
      case 'dev_beta_revision':
        if (!internal) {
          return '#48BF91';
        } else {
          return '#FF55BF';
        }
      case 'dev_final':
        return '#013BB1';
      case 'prod_alpha_internal':
        if (!internal) {
          return '#80F2E7';
        } else {
          return '#8A2FFF';
        }
      case 'prod_alpha':
        return '#80F2E7';
      case 'prod_alpha_revision':
        if (!internal) {
          return '#80F2E7';
        } else {
          return '#8A2FFF';
        }
      case 'prod_beta':
        return '#48BF91';
      case 'prod_beta_revision':
        if (!internal) {
          return '#48BF91';
        } else {
          return '#8A2FFF';
        }
      case 'prod_final':
        return '#013BB1';
      case 'trials_pre':
        return '#FF5454';
      default:
        return '#FF55BF'; // default color (SB internal)
    }
  };

  const handleReviewIcon = (review) => {
    // split string with underscore and get the first element
    const review_type = review.split('_')[0];

    switch (review_type) {
      case 'prod':
        return 'heroicons-outline:desktop-computer';
      default:
        return 'heroicons-outline:document-text';
    }
  };

  return (
    <div className="w-full">
      <Stack direction="row" spacing={1}>
        <Chip
          className="capitalize font-semibold text-10"
          label={handleReviewLabel(comment?.review?.cycle, internality)}
          size="small"
          icon={
            <BASESvgIcon
              size={16}
              sx={{
                color:
                  comment?.review?.cycle === 'dev_alpha' ||
                  comment?.review?.cycle === 'prod_alpha' ||
                  (comment?.review?.cycle === 'dev_alpha_internal' &&
                    internality === false) ||
                  (comment?.review?.cycle === 'dev_alpha_revision' &&
                    internality === false) ||
                  (comment?.review?.cycle === 'prod_alpha_internal' &&
                    internality === false) ||
                  (comment?.review?.cycle === 'prod_alpha_revision' &&
                    internality === false) || 
                  comment?.review?.cycle === 'trials_pre'
                    ? '#111827 !important'
                    : '#ffffff !important'
              }}
            >
              {handleReviewIcon(comment?.review?.cycle)}
            </BASESvgIcon>
          }
          sx={{
            backgroundColor: handleReviewColor(
              comment?.review?.cycle,
              internality
            ),
            color:
              comment?.review?.cycle === 'dev_alpha' ||
              comment?.review?.cycle === 'prod_alpha' ||
              (comment?.review?.cycle === 'dev_alpha_internal' &&
                internality === false) ||
              (comment?.review?.cycle === 'dev_alpha_revision' &&
                internality === false) ||
              (comment?.review?.cycle === 'prod_alpha_internal' &&
                internality === false) ||
              (comment?.review?.cycle === 'prod_alpha_revision' &&
                internality === false) || 
              comment?.review?.cycle === 'trials_pre'
                ? '#111827'
                : '#ffffff'
          }}
        />
        {comment.scope && (
          <Chip
            label={comment.scope}
            size="small"
            className="capitalize font-semibold text-10"
            icon={
              <BASESvgIcon size={16}>
                {handleScopeIcon(comment.scope)}
              </BASESvgIcon>
            }
          />
        )}
      </Stack>
    </div>
  );
}

export default CommentStatusBar;
